/**
 * shiki-transformers.js
 *
 * Custom shiki transformer that eliminates per-token `style=` attributes so
 * the rendered HTML is clean under a `style-src 'self'` CSP policy.
 *
 * Strategy (DECISION-1-B in design):
 *   Each token span emitted by shiki carries `style="color: #rrggbb"`.
 *   This transformer:
 *     1. Maps the hex color to a stable `shiki-tok-N` class name via a
 *        deterministic lookup table (same hex → same class, always).
 *     2. Adds the class to the span's `class` list.
 *     3. Deletes `properties.style` so no `style=` attribute is emitted.
 *
 * The color table is derived from the github-dark theme palette — the only
 * theme loaded by useShiki.js.  If the theme changes, update COLOR_CLASS_MAP.
 *
 * CSS classes (.shiki-tok-0 … .shiki-tok-N) are defined in
 * app/frontend/entrypoints/application.css.
 */

/**
 * Deterministic map: hex color (lowercase) → CSS class name.
 * Derived from shiki@latest + github-dark theme across all supported langs.
 *
 * Colors confirmed via extraction (see praxis/csp-style-src-hardening/design):
 *   #e1e4e8 — default text / plain
 *   #f97583 — keywords, operators
 *   #79b8ff — built-ins, variables, numbers
 *   #9ecbff — strings
 *   #b392f0 — functions, identifiers
 *   #ffab70 — parameters, constants
 *   #85e89d — tags, declarations
 *   #6a737d — comments
 */
const COLOR_CLASS_MAP = {
  "#e1e4e8": "shiki-tok-0", // default / plain text
  "#f97583": "shiki-tok-1", // keywords, operators
  "#79b8ff": "shiki-tok-2", // built-ins, variables, numbers
  "#9ecbff": "shiki-tok-3", // strings
  "#b392f0": "shiki-tok-4", // functions, identifiers
  "#ffab70": "shiki-tok-5", // parameters, constants
  "#85e89d": "shiki-tok-6", // tags, declarations
  "#6a737d": "shiki-tok-7", // comments
};

/**
 * Extract the first hex color from a CSS style string.
 * Handles both `color: #rrggbb` and `color:#rrggbb`.
 *
 * @param {string} style - The style attribute string.
 * @returns {string|null} Lowercase hex color or null if not found.
 */
function extractHexColor(style) {
  if (!style) return null;
  const match = style.match(/#([0-9a-fA-F]{3,8})/);
  return match ? match[0].toLowerCase() : null;
}

/**
 * Resolve or generate a CSS class for a given hex color.
 * Unknown colors (from future theme changes) fall back to `shiki-tok-unknown`.
 *
 * @param {string} hex - Lowercase hex color string (e.g. "#e1e4e8").
 * @returns {string} CSS class name.
 */
function classForColor(hex) {
  return COLOR_CLASS_MAP[hex] ?? "shiki-tok-unknown";
}

/**
 * stripInlineStyleTransformer — shiki transformer factory.
 *
 * Usage:
 *   highlighter.codeToHtml(code, {
 *     lang,
 *     theme: 'github-dark',
 *     transformers: [stripInlineStyleTransformer()],
 *   })
 *
 * @returns {import('shiki').ShikiTransformer}
 */
export function stripInlineStyleTransformer() {
  return {
    name: "anamnesis:strip-inline-style",

    /**
     * Called once per token span in the rendered HAST tree.
     * Replaces `style="color: #rrggbb"` with a `shiki-tok-N` class.
     *
     * @param {import('hast').Element} node - HAST span element.
     */
    span(node) {
      const style = node.properties?.style;
      if (!style) return;

      const hex = extractHexColor(style);
      if (hex) {
        const cls = classForColor(hex);
        const existing = node.properties.class ?? [];
        node.properties.class = Array.isArray(existing)
          ? [...existing, cls]
          : [existing, cls];
      }

      // Always remove the inline style attribute — even for unmapped colors.
      delete node.properties.style;
    },
  };
}
